/**
 * useDecisions — local mirror of every change's review status with
 * optimistic mutations (M3 Phase 3.6 / BR5).
 *
 * Why a hook (and not a redux/zustand store):
 *   - There is exactly one consumer per page (`ComparisonWorkspace`).
 *   - Concurrent mutations are bounded to the visible change list.
 *   - The wider app does not need to read decision state.
 *
 * Mutation flow per BR5:
 *
 *   1. Caller invokes `decide(changeId, action)`.
 *   2. We *optimistically* set the local status to the desired value
 *      (toggling back to pending when the user re-asserts the same verb).
 *   3. The PUT request fires in parallel.
 *   4a. On success — overwrite with the server's authoritative status.
 *       This matters because the server may return `pending` (toggle) or a
 *       different status if a concurrent reviewer raced ahead of us.
 *   4b. On failure — rollback to whatever we had before step 2 + surface a
 *       toast. We rollback to the *snapshot* taken before the PUT so that
 *       chained mutations don't lose intermediate state.
 *
 * Double-click guard:
 *   The `pendingMap` records changeIds with an in-flight PUT. The UI uses
 *   `isPending(id)` to disable just that change's buttons; the rest of the
 *   panel stays interactive (per the brief — never disable the whole panel).
 *
 * `refresh()` exists for cross-role visibility: switching from lawyer to
 * reviewer needs to pick up the lawyer's pending decisions even if no PUT
 * happened in this tab. Callers can debounce / re-call on focus events.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  veridocApi,
  type ChangeResponse,
  type DecisionAction,
  type DecisionMutationResponse,
  type DecisionStatus,
  type ReviewDecisionResponse,
} from '../../lib/veridoc-api';

interface UseDecisionsOptions {
  comparisonId: string | undefined;
  initialChanges: ChangeResponse[];
  /** Toast hook callback — pluggable so tests / Storybook can stub it out. */
  onError?: (message: string) => void;
  /**
   * Fired after a SINGLE decision is persisted (server-confirmed), with the
   * authoritative final status — never on the optimistic update and never on
   * rollback. Symmetric to `onError`. Pluggable so a host-facing consumer
   * (the embed shell's `onDecision` prop) can observe decisions without this
   * hook knowing anything about postMessage. `batchDecide` intentionally does
   * NOT fire this — batch events are a separate, R4 concern.
   */
  onDecision?: (changeId: string, finalStatus: DecisionStatus) => void;
}

export interface DecisionMeta {
  /** Display name of the reviewer who recorded the latest decision. */
  reviewerDisplayName: string;
  /** ISO timestamp of the decision. */
  decidedAt: string;
  /**
   * P2-6: optional reviewer rationale. `null` when none was recorded.
   * Surface in the decision overlay so the reviewer / collaborators can
   * see *why* a change was accepted or rejected without leaving the row.
   */
  note: string | null;
}

export interface UseDecisionsResult {
  getStatus: (changeId: string) => DecisionStatus;
  getMeta: (changeId: string) => DecisionMeta | null;
  isPending: (changeId: string) => boolean;
  decide: (changeId: string, action: DecisionAction, note?: string) => Promise<void>;
  batchDecide: (changeIds: string[], action: DecisionAction) => Promise<void>;
  batchPending: boolean;
  refresh: () => Promise<void>;
  /**
   * Monotonic counter bumped on every status/meta mutation. Downstream
   * memos that need to react to decision changes (progress badge, minimap
   * aggregation) depend on this instead of the unstable status/meta maps.
   */
  statusVersion: number;
  /** Aggregate counts for the top-bar progress badge. */
  progress: {
    decided: number;
    accepted: number;
    rejected: number;
    total: number;
  };
}

function buildInitialStatusMap(changes: ChangeResponse[]): Map<string, DecisionStatus> {
  const map = new Map<string, DecisionStatus>();
  for (const change of changes) {
    map.set(change.id, change.currentStatus ?? 'pending');
  }
  return map;
}

export function useDecisions({
  comparisonId,
  initialChanges,
  onError,
  onDecision,
}: UseDecisionsOptions): UseDecisionsResult {
  // Status map — re-seeded whenever the source comparison changes (e.g. the
  // page navigates from one comparison to another in the same SPA session).
  const [statusMap, setStatusMap] = useState<Map<string, DecisionStatus>>(
    () => buildInitialStatusMap(initialChanges),
  );
  const [metaMap, setMetaMap] = useState<Map<string, DecisionMeta>>(
    () => new Map(),
  );
  // Tracks in-flight PUTs by changeId so the UI can disable specifically
  // those buttons. `pendingMap` is the render-trigger side: `setPending` swaps
  // in a new Set so React re-renders. Reads happen through `pendingRef`
  // (below) so `isPending` keeps a stable identity.
  const [pendingMap, setPendingMap] = useState<Set<string>>(() => new Set());
  void pendingMap; // render-trigger only; reads go through pendingRef
  const pendingRef = useRef<Set<string>>(new Set());
  const [batchPending, setBatchPending] = useState(false);

  // Ref mirrors of the three state maps. Reads inside the five consumer
  // callbacks (getStatus / getMeta / isPending / decide / batchDecide) go
  // through these so the callbacks can have empty dependency arrays — their
  // identities stay stable across every decision. Without this, every
  // decision rebuilt every callback, cascading through ~1000-2500
  // DecidableChange / ChangeMarker / NodeBlock re-renders (T1 baseline).
  const statusMapRef = useRef(statusMap);
  statusMapRef.current = statusMap;
  const metaMapRef = useRef(metaMap);
  metaMapRef.current = metaMap;

  // Ref mirrors of the host-facing callbacks. Without these, an inline
  // `onError` / `onDecision` passed by a host integration (e.g. an embed
  // shell's `onDecision` prop) would flip identity on every render →
  // `decide` would rebuild → `decisionAdapter` would rebuild → every
  // `DecidableChange` would re-render on each host render. Reading via the
  // ref keeps `decide` identity stable while still invoking whatever the
  // host currently passed. Mirrors the same pattern used for `onReady` /
  // `onLoadError` / `user` in `ComparisonWorkspace.tsx`.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onDecisionRef = useRef(onDecision);
  onDecisionRef.current = onDecision;

  // Monotonic counter — bumped by `applyDecisionRow` so memos that depend on
  // decision state (progress, statusVersion consumers) can recompute without
  // depending on the unstable map references.
  const [statusVersion, setStatusVersion] = useState(0);

  const totalRef = useRef<number>(initialChanges.length);

  // Re-seed when the inputs swap (different comparison or freshly fetched
  // payload). The `initialChanges` reference identity changes on each fetch
  // so we use length + first/last id as a cheap fingerprint.
  const fingerprint = useMemo(() => {
    if (initialChanges.length === 0) return 'empty';
    const head = initialChanges[0]!.id;
    const tail = initialChanges[initialChanges.length - 1]!.id;
    return `${comparisonId ?? '∅'}|${initialChanges.length}|${head}|${tail}`;
  }, [comparisonId, initialChanges]);

  useEffect(() => {
    setStatusMap(buildInitialStatusMap(initialChanges));
    setMetaMap(new Map());
    totalRef.current = initialChanges.length;
    // Don't reset pendingMap — an in-flight PUT crossing a fetch is fine
    // because the response will overwrite the new map too.
    // Bump version so progress recomputes against the freshly-seeded map.
    setStatusVersion((v) => v + 1);
  }, [fingerprint, initialChanges]);

  const getStatus = useCallback(
    (changeId: string): DecisionStatus =>
      statusMapRef.current.get(changeId) ?? 'pending',
    [],
  );

  const getMeta = useCallback(
    (changeId: string): DecisionMeta | null =>
      metaMapRef.current.get(changeId) ?? null,
    [],
  );

  const isPending = useCallback(
    (changeId: string): boolean => pendingRef.current.has(changeId),
    [],
  );

  const setPending = useCallback((changeId: string, pending: boolean) => {
    if (pending) pendingRef.current.add(changeId);
    else pendingRef.current.delete(changeId);
    setPendingMap(new Set(pendingRef.current));
  }, []);

  /**
   * Apply a server-authoritative response to the local maps. Used for
   * both individual PUT responses and bulk `refresh()` results. Bumps
   * `statusVersion` so memos keyed on it (progress badge) recompute.
   */
  const applyDecisionRow = useCallback(
    (changeId: string, status: DecisionStatus, meta: DecisionMeta | null) => {
      setStatusMap((prev) => {
        const next = new Map(prev);
        next.set(changeId, status);
        return next;
      });
      setMetaMap((prev) => {
        const next = new Map(prev);
        if (meta && status !== 'pending') next.set(changeId, meta);
        else next.delete(changeId);
        return next;
      });
      setStatusVersion((v) => v + 1);
    },
    [],
  );

  const decide = useCallback(
    async (changeId: string, action: DecisionAction, note?: string): Promise<void> => {
      // Double-click guard — skip silently. Status hasn't changed so the UI
      // doesn't need a toast or visual feedback.
      if (pendingRef.current.has(changeId)) return;

      const before = statusMapRef.current.get(changeId) ?? 'pending';
      const beforeMeta = metaMapRef.current.get(changeId) ?? null;
      const trimmedNote = note?.trim() || undefined;

      // Optimistic target: 'pending' always resets; accept/reject toggle
      // back to pending when re-asserting the current state (matches server
      // toggle semantics so the UI never shows a state the server can't reach).
      // P2-6 exception: when the caller passes a non-empty note, the server
      // skips the toggle-off check — so a same-status PUT updates the
      // rationale in place rather than flipping to pending. The optimistic
      // branch mirrors that so the UI does not briefly show pending while
      // the request is in flight.
      const optimistic: DecisionStatus = (() => {
        if (action === 'pending') return 'pending';
        const targetForAction: DecisionStatus =
          action === 'accept' ? 'accepted' : 'rejected';
        if (trimmedNote && before === targetForAction) return before;
        return before === targetForAction ? 'pending' : targetForAction;
      })();

      applyDecisionRow(
        changeId,
        optimistic,
        // Optimistic meta clears on toggle-to-pending; for a fresh accept
        // / reject we don't know the reviewer yet so leave meta untouched
        // and let the response fill it in.
        optimistic === 'pending' ? null : beforeMeta,
      );
      setPending(changeId, true);

      try {
        const response: DecisionMutationResponse = await veridocApi.putDecision(
          changeId,
          action,
          trimmedNote,
        );
        let finalStatus: DecisionStatus;
        if (response.status === 'pending') {
          applyDecisionRow(changeId, 'pending', null);
          finalStatus = 'pending';
        } else {
          const decision = response as ReviewDecisionResponse;
          applyDecisionRow(changeId, decision.status, {
            reviewerDisplayName: decision.reviewerDisplayName,
            decidedAt: decision.decidedAt,
            note: decision.note,
          });
          finalStatus = decision.status;
        }
        // Only after the server confirms — never on the optimistic write or a
        // rollback. Host-facing observers (embed `onDecision` prop) rely on
        // this being the authoritative landed state. Read via ref so `decide`
        // identity stays stable across host re-renders.
        onDecisionRef.current?.(changeId, finalStatus);
      } catch (err) {
        // Rollback on failure. We rollback against the snapshot taken
        // before this call rather than against the live state to preserve
        // any concurrent writes that landed during our PUT.
        applyDecisionRow(changeId, before, beforeMeta);
        const verb = action === 'accept' ? '接受' : action === 'reject' ? '拒绝' : '重置';
        const message =
          err instanceof Error
            ? `${verb}变更失败：${err.message}`
            : `${verb}变更失败`;
        onErrorRef.current?.(message);
      } finally {
        setPending(changeId, false);
      }
    },
    [applyDecisionRow, setPending],
  );

  const batchDecide = useCallback(
    async (changeIds: string[], action: DecisionAction): Promise<void> => {
      if (changeIds.length === 0) return;

      // Skip ids already in the target state — avoids unnecessary API calls
      // and prevents toggling them back to pending.
      const targetStatus: DecisionStatus =
        action === 'pending' ? 'pending' : action === 'accept' ? 'accepted' : 'rejected';
      const toProcess = changeIds.filter(
        (id) => (statusMapRef.current.get(id) ?? 'pending') !== targetStatus,
      );
      if (toProcess.length === 0) return;

      setBatchPending(true);

      // Snapshot originals for rollback.
      const snapshots = new Map<string, { status: DecisionStatus; meta: DecisionMeta | null }>();
      for (const id of toProcess) {
        snapshots.set(id, {
          status: statusMapRef.current.get(id) ?? 'pending',
          meta: metaMapRef.current.get(id) ?? null,
        });
      }

      // Optimistic update for all ids at once.
      for (const id of toProcess) {
        applyDecisionRow(id, targetStatus, snapshots.get(id)!.meta);
      }

      // Fire all PUTs concurrently; collect results without short-circuiting.
      const results = await Promise.allSettled(
        toProcess.map((id) => veridocApi.putDecision(id, action)),
      );

      let failCount = 0;
      for (let i = 0; i < toProcess.length; i++) {
        const id = toProcess[i]!;
        const result = results[i]!;
        if (result.status === 'fulfilled') {
          const response = result.value;
          if (response.status === 'pending') {
            applyDecisionRow(id, 'pending', null);
          } else {
            const decision = response as import('../../lib/veridoc-api').ReviewDecisionResponse;
            applyDecisionRow(id, decision.status, {
              reviewerDisplayName: decision.reviewerDisplayName,
              decidedAt: decision.decidedAt,
              note: decision.note,
            });
          }
        } else {
          // Rollback this id to its pre-batch snapshot.
          const snap = snapshots.get(id)!;
          applyDecisionRow(id, snap.status, snap.meta);
          failCount += 1;
        }
      }

      if (failCount > 0) {
        onErrorRef.current?.(`${failCount} 条操作失败，已回滚`);
      }

      setBatchPending(false);
    },
    [applyDecisionRow],
  );

  const refresh = useCallback(async (): Promise<void> => {
    if (!comparisonId) return;
    try {
      const { decisions } = await veridocApi.getDecisions(comparisonId);
      // Build new maps based on the server's authoritative list. Any
      // change not in the response is `pending` (the API returns *only*
      // active rows).
      setStatusMap(() => {
        const next = new Map<string, DecisionStatus>();
        for (const id of changeIdsFromChanges(initialChanges)) {
          next.set(id, 'pending');
        }
        for (const decision of decisions) {
          next.set(decision.changeId, decision.status);
        }
        return next;
      });
      setMetaMap(() => {
        const next = new Map<string, DecisionMeta>();
        for (const decision of decisions) {
          next.set(decision.changeId, {
            reviewerDisplayName: decision.reviewerDisplayName,
            decidedAt: decision.decidedAt,
            note: decision.note,
          });
        }
        return next;
      });
      // Bump version so progress / statusVersion-keyed memos recompute after
      // the wholesale map replacement above (which bypasses applyDecisionRow).
      setStatusVersion((v) => v + 1);
    } catch (err) {
      const message =
        err instanceof Error
          ? `刷新审阅状态失败：${err.message}`
          : '刷新审阅状态失败';
      onErrorRef.current?.(message);
    }
  }, [comparisonId, initialChanges]);

  const progress = useMemo(() => {
    // Read from the ref so this memo's identity only flips when statusVersion
    // bumps — not on every statusMap replacement. The `void statusVersion`
    // line is the dependency hook; the ref read happens at compute time.
    void statusVersion;
    let accepted = 0;
    let rejected = 0;
    for (const status of statusMapRef.current.values()) {
      if (status === 'accepted') accepted += 1;
      else if (status === 'rejected') rejected += 1;
    }
    return {
      accepted,
      rejected,
      decided: accepted + rejected,
      total: totalRef.current,
    };
  }, [statusVersion]);

  return useMemo(
    () => ({
      getStatus,
      getMeta,
      isPending,
      decide,
      batchDecide,
      batchPending,
      refresh,
      progress,
      statusVersion,
    }),
    [
      getStatus,
      getMeta,
      isPending,
      decide,
      batchDecide,
      batchPending,
      refresh,
      progress,
      statusVersion,
    ],
  );
}

/** Helper kept outside the closure so refresh()'s reducer reads a stable
 * value rather than capturing the live `statusMap`. */
function changeIdsFromChanges(changes: ChangeResponse[]): string[] {
  return changes.map((c) => c.id);
}
